import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLoginUserMutation } from '../../redux/services/userService'
import { useAppDispatch, useAppSelector } from '../../redux/hooks'
import { setCredentials } from '../../redux/slices/userSlice'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { nasccLogo } from '../../assets'

export default function AdminLogin() {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const user = useAppSelector((state) => state.user)
  const [loginUser, { isLoading }] = useLoginUserMutation()
  const [loginSuccess, setLoginSuccess] = useState(false)

  const [formData, setFormData] = useState({
    email: '',
    password: ''
  })

  // Handle navigation after successful login and state update
  useEffect(() => {
    if (
      loginSuccess &&
      user.token &&
      user.roles?.some((role) =>
        typeof role === 'string'
          ? role.toLowerCase() === 'admin'
          : (role as { name?: string }).name?.toLowerCase() === 'admin'
      )
    ) {
      console.log('useEffect: User state updated, navigating to admin...')
      navigate('/admin')
      setLoginSuccess(false) // Reset the flag
    }
  }, [loginSuccess, user.token, user.roles, navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      const result = await loginUser({
        username: formData.email,
        password: formData.password
      }).unwrap()

      if (
        result.roles?.some((role) =>
          typeof role === 'string'
            ? role.toLowerCase() === 'admin'
            : (role as { name?: string }).name?.toLowerCase() === 'admin'
        )
      ) {
        dispatch(setCredentials(result))
        setLoginSuccess(true) // Trigger the useEffect navigation
      } else {
        toast.error('You do not have admin access')
      }
    } catch {
      toast.error('Invalid email or password')
    }
  }

  return (
    <div className="flex flex-col items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        pauseOnHover
      />
      {/* Logo Header */}
      <div className="bg-white p-6 mt-8 sm:mx-auto sm:w-full sm:max-w-md sm:rounded-lg sm:shadow-lg">
        <img src={nasccLogo} alt="NASCC Logo" className="h-20 mx-auto" />
      </div>

      {/* Login Form */}
      <form
        className="mt-6 sm:mx-auto sm:w-full sm:max-w-md"
        onSubmit={handleSubmit}
      >
        <fieldset className="bg-white py-8 px-4 shadow sm:rounded-lg sm:shadow-lg sm:px-10">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-semibold text-[#3C2416]">
              Admin Login
            </h2>
            <p className="mt-2 text-sm text-[#9298a8]">
              Sign in to access the NASCC admin dashboard
            </p>
          </div>

          <div className="space-y-6">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-[#3C2416]"
              >
                Email address
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#A6410A] focus:border-[#A6410A] sm:text-sm"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-[#3C2416]"
              >
                Password
              </label>
              <div className="mt-1">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#A6410A] focus:border-[#A6410A] sm:text-sm"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#A6410A] hover:bg-[#894d35] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#A6410A] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? (
                  <span className="flex items-center">
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Signing in...
                  </span>
                ) : (
                  'Sign in'
                )}
              </button>
            </div>

            <div className="mt-4 text-center">
              <a
                href="/"
                className="text-sm text-[#A6410A] hover:text-[#894d35]"
              >
                Back to main site
              </a>
            </div>
          </div>
        </fieldset>
      </form>
    </div>
  )
}
